import { Spinner } from '@workspace/ui/components/Spinner';
import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import React, { useMemo } from 'react';

const variant = {
  primary: 'disabled:border-0',
  primaryOutlined: 'disabled:bg-transparent disabled:border-stroke-tertiary',
  brand:
    'border-action-brand bg-action-brand text-white hover:bg-action-brand-hover disabled:border-0',
  secondary:
    'border-transparent bg-surface-transparent-2 hover:bg-surface-transparent-3',
  outlined:
    'border-stroke-primary bg-transparent hover:bg-surface-transparent-2 disabled:border-stroke-tertiary disabled:shadow-none',
  shadowS:
    'hover:bg-action-neutral-hover disabled:border-stroke-tertiary disabled:shadow-none',
  shadowXS: 'disabled:border-stroke-tertiary disabled:shadow-none',
} as const;

const size = {
  xs: 'px-3 py-1.5 gap-1',
  sm: 'max-h-9 px-4 py-2.5 gap-1',
  md: 'max-h-10 py-3 px-4 gap-1.5',
  lg: 'max-h-12 py-4 px-5 gap-2',
  xl: 'h-14 py-4 px-6 gap-2.5',
} as const;

const buttonVariants = cva(
  'inline-flex items-center justify-center shrink-0 appearance-none border cursor-pointer select-none rounded-full transition-[background-color,box-shadow] duration-[256ms] ease-main focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:text-text-disabled',
  {
    variants: {
      variant,
      size,
    },
    compoundVariants: [
      {
        variant: ['primary', 'primaryOutlined'],
        className:
          'border-action-primary bg-action-primary text-white hover:bg-action-primary-hover',
      },
      {
        variant: ['primary', 'brand'],
        className: 'disabled:bg-surface-transparent-2',
      },
      {
        variant: ['outlined', 'shadowXS', 'shadowS'],
        className:
          'border-stroke-primary bg-transparent text-text-primary',
      },
      {
        variant: ['shadowXS', 'shadowS'],
        className: 'border-stroke-primary bg-graphic-regular-white shadow-sm',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    isLoading?: boolean;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      variant = 'primary',
      size = 'md',
      asChild = false,
      startIcon,
      endIcon,
      isLoading,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot.Root : 'button';

    const TypographyComponent = useMemo(() => {
      switch (size) {
        case 'xs':
          return Typography.Text100Semibold;
        case 'sm':
          return Typography.Text200Semibold;
        case 'md':
          return Typography.Text200Semibold;
        case 'lg':
          return Typography.Text300Semibold;
        case 'xl':
          return Typography.Text400Semibold;
        default:
          return Typography.Text200Semibold;
      }
    }, [size]);

    const resolvedStartIcon = isLoading ? <Spinner size={size} /> : startIcon;

    return (
      <Comp
        ref={ref}
        {...props}
        data-slot="button"
        data-variant={variant}
        aria-busy={isLoading || undefined}
        data-size={size}
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={disabled || isLoading}
      >
        {resolvedStartIcon && (
          <span data-slot="start-icon">{resolvedStartIcon}</span>
        )}
        {children && (
          <TypographyComponent as="span">{children}</TypographyComponent>
        )}
        {endIcon && <span data-slot="end-icon">{endIcon}</span>}
      </Comp>
    );
  },
);

Button.displayName = 'Button';

export { Button, buttonVariants, variant, size };
